// Apolo IDE — useAutoPR (Sprint v11 E12).
//
// One-click worktree → PR flow:
//   1. Read git status + diff via Electron IPC (window.apolo.git.*)
//   2. POST diff to Worker /auto-pr/draft for commit msg + PR body
//   3. Show confirmation modal (caller responsibility — this hook returns
//      a `prepare()` that resolves with the draft for review)
//   4. After user OK: Electron commits + pushes, then Worker creates the PR

import { useCallback, useState } from 'react';
import { hasElectronBridge } from '../lib/electronBridge.js';
import { apiFetch } from '../lib/api.js';
import { syncExternalSignalFromGit, syncMergeSignalFromPR } from '../lib/finalizeExternalOutcome.js';
import { patchOutcomePrNumber } from '../lib/runOutcomeBridge.js';

async function gitStatus(root) {
  if (!hasElectronBridge() || !window.apolo?.git?.status) return null;
  try { return await window.apolo.git.status({ root }); } catch { return null; }
}
async function gitDiff(root) {
  if (!hasElectronBridge() || !window.apolo?.git?.diff) return null;
  try { return await window.apolo.git.diff({ root }); } catch { return null; }
}
async function gitCommit(root, message) {
  if (!hasElectronBridge() || !window.apolo?.git?.commit) throw new Error('Electron git.commit ausente');
  return window.apolo.git.commit({ root, message });
}
async function gitPush(root, branch) {
  if (!hasElectronBridge() || !window.apolo?.git?.push) throw new Error('Electron git.push ausente');
  return window.apolo.git.push({ root, branch });
}

/**
 * @param {{ sessionId: string, root: string, branch?: string, base?: string, owner?: string, repo?: string }} params
 */
export function useAutoPR({ sessionId, root, branch = 'feature', base = 'main', owner, repo }) {
  const [phase, setPhase] = useState('idle'); // idle|preparing|ready|committing|pushing|opening|done|error
  const [draft, setDraft] = useState(null); // { commit_message, pr_title, pr_body, files, diff }
  const [error, setError] = useState(null);
  const [prUrl, setPrUrl] = useState(null);

  const prepare = useCallback(async () => {
    setError(null);
    setPhase('preparing');
    setDraft(null);
    setPrUrl(null);
    if (!sessionId) {
      setError('Sessão inválida (sem sessionId).');
      setPhase('error');
      return null;
    }
    if (!root) {
      setError('Esta sessão não tem worktree git. Auto PR só funciona em sessões criadas com "Worktree isolado" — abre uma nova sessão marcando essa opção.');
      setPhase('error');
      return null;
    }
    const status = await gitStatus(root);
    const diff = await gitDiff(root);
    if (!diff) {
      setError('Sem mudanças (worktree limpo) ou git IPC indisponível.');
      setPhase('error');
      return null;
    }
    const files = Array.isArray(status?.files) ? status.files.map((f) => f.path || f) : [];
    const res = await apiFetch(`/api/code-ide/sessions/${sessionId}/auto-pr/draft`, {
      method: 'POST',
      body: { diff, files },
    });
    if (!res.ok) {
      setError(res.error || 'Falha ao gerar draft');
      setPhase('error');
      return null;
    }
    const next = { ...res.data, files, diff };
    setDraft(next);
    setPhase('ready');
    return next;
  }, [sessionId, root]);

  const confirmAndShip = useCallback(async (override) => {
    const final = { ...(draft || {}), ...(override || {}) };
    if (!final.commit_message || !final.pr_title) {
      setError('commit_message e pr_title obrigatórios');
      setPhase('error');
      return null;
    }
    try {
      setPhase('committing');
      await gitCommit(root, final.commit_message);
      setPhase('pushing');
      await gitPush(root, branch);
      setPhase('opening');
      if (!owner || !repo) {
        setError('owner + repo necessários no useAutoPR config pra abrir o PR');
        setPhase('error');
        return null;
      }
      const prRes = await apiFetch(`/api/code-ide/sessions/${sessionId}/pr`, {
        method: 'POST',
        body: {
          owner, repo,
          head: branch, base,
          title: final.pr_title,
          body: `${final.pr_body || ''}\n\n---\n_Generated with Apolo IDE._`,
        },
      });
      if (!prRes.ok) {
        setError(prRes.error || 'Falha ao abrir PR');
        setPhase('error');
        return null;
      }
      const url = prRes.data?.pull_request?.html_url;
      const prNumber = prRes.data?.pull_request?.number;
      setPrUrl(url);
      setPhase('done');
      // Remember the PR number on the run's outcome row so a later remote merge
      // can back-fill the STRONG human_merged signal (local git never sees it).
      if (Number.isInteger(prNumber)) void patchOutcomePrNumber(sessionId, prNumber);
      void syncExternalSignalFromGit(sessionId, root);
      // After (not instead of) the git-evidence sync: try the remote merge half.
      // Right after opening the PR it isn't merged yet, so this is a no-op now —
      // but the same call run on a later session reopen back-fills human_merged
      // once a human merges the PR (the reopen sync below re-invokes it).
      if (Number.isInteger(prNumber) && owner && repo) {
        void syncMergeSignalFromPR(sessionId, { pr_number: prNumber, owner, repo });
      }
      return url;
    } catch (e) {
      setError(e?.message || 'Falha no auto-PR');
      setPhase('error');
      return null;
    }
  }, [draft, root, branch, base, owner, repo, sessionId]);

  return { prepare, confirmAndShip, phase, draft, error, prUrl, setDraft };
}
